import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import QuizBulkImportPanel from './QuizBulkImportPanel';


const QuizManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [mode, setMode] = useState('date');
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [quizDates, setQuizDates] = useState([]);
  const [quizSettings, setQuizSettings] = useState({ 
    timeLimit: 600,
    maxAttempts: null
  });
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPoolSettings, setShowPoolSettings] = useState(false);
  const [poolSettings, setPoolSettings] = useState({
    isEnabled: false,
    questionsPerDay: 5,
    categories: []
  });

  // Bulk Import States
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImportError, setBulkImportError] = useState(null);
  
  const [formData, setFormData] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    category: 'general',
    difficulty: 'medium',
    mode: 'date',
    quizDate: new Date().toISOString().split('T')[0]
  });


  useEffect(() => {
    fetchCategories();
    fetchPoolSettings();
  }, []);


  useEffect(() => {
    if (mode === 'date') {
      fetchQuestionsByDate(selectedDate);
      fetchQuizDates();
      fetchQuizSettings(selectedDate);
    } else if (mode === 'pool') {
      fetchPoolQuestions();
    }
  }, [mode, selectedDate, selectedCategory]);


  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([
        { name: 'general', displayName: 'General Sports' },
        { name: 'football', displayName: 'Football' },
        { name: 'cricket', displayName: 'Cricket' },
        { name: 'basketball', displayName: 'Basketball' },
        { name: 'tennis', displayName: 'Tennis' },
        { name: 'hockey', displayName: 'Hockey' },
        { name: 'rugby', displayName: 'Rugby' },
        { name: 'golf', displayName: 'Golf' },
        { name: 'boxing', displayName: 'Boxing' },
        { name: 'other', displayName: 'Other' }
      ]);
    }
  };


  const fetchPoolSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/pool-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data._id) {
        setPoolSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching pool settings:', error);
    }
  };


  const fetchQuestionsByDate = async (date) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/questions/date/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchPoolQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      let url = `${API_BASE_URL}/api/admin/quiz/questions-by-mode?mode=pool`;
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching pool questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchQuizDates = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizDates(response.data);
    } catch (error) {
      console.error('Error fetching quiz dates:', error);
    }
  };


  const fetchQuizSettings = async (date) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/settings/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const settings = response.data || {};
      setQuizSettings({ 
        timeLimit: settings.timeLimit || null,
        maxAttempts: settings.maxAttempts || null
      });
    } catch (error) {
      console.error('Error fetching quiz settings:', error);
      setQuizSettings({ timeLimit: null, maxAttempts: null });
    }
  };


  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_BASE_URL}/api/admin/quiz/settings`, {
        quizDate: selectedDate,
        timeLimit: quizSettings.timeLimit === '' || quizSettings.timeLimit === null ? null : quizSettings.timeLimit,
        maxAttempts: quizSettings.maxAttempts === '' || quizSettings.maxAttempts === null ? null : quizSettings.maxAttempts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Quiz settings saved successfully!');
      setShowSettingsForm(false);
      fetchQuizSettings(selectedDate);
    } catch (error) {
      alert(`Failed to save settings: ${error.response?.data?.message || error.message}`);
    }
  };


  const handlePoolSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_BASE_URL}/api/admin/quiz/pool-settings`, poolSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Pool settings saved successfully!');
      setShowPoolSettings(false);
      fetchPoolSettings();
    } catch (error) {
      alert(`Failed to save pool settings: ${error.response?.data?.message || error.message}`);
    }
  };


  const handleBulkImport = async () => {
    setBulkImportError(null);
    setBulkImportResult(null);

    try {
      const data = JSON.parse(bulkImportData);
      
      if (!data.category || !data.questions || !Array.isArray(data.questions)) {
        setBulkImportError({
          message: 'Invalid format: category and questions array are required',
          errors: []
        });
        return;
      }

      if (data.questions.length === 0) {
        setBulkImportError({
          message: 'Questions array cannot be empty',
          errors: []
        });
        return;
      }

      setBulkImportLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/quiz/questions/bulk-import`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setBulkImportResult({
        success: true,
        message: response.data.message,
        count: response.data.count
      });
      
      setBulkImportData('');
      
      // Refresh questions
      if (mode === 'date') {
        fetchQuestionsByDate(selectedDate);
        fetchQuizDates();
      } else {
        fetchPoolQuestions();
      }
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        setShowBulkImport(false);
        setBulkImportResult(null);
      }, 2000);
      
    } catch (err) {
      if (err.response?.data?.errors) {
        setBulkImportError({
          message: err.response.data.message,
          errors: err.response.data.errors
        });
      } else if (err instanceof SyntaxError) {
        setBulkImportError({
          message: 'Invalid JSON format. Check your input.',
          errors: [err.message]
        });
      } else {
        setBulkImportError({
          message: err.response?.data?.message || 'Failed to import questions',
          errors: []
        });
      }
    } finally {
      setBulkImportLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const submitData = {
        ...formData,
        mode: formData.mode || mode
      };


      if (submitData.mode === 'pool') {
        delete submitData.quizDate;
      }


      if (editingQuestion) {
        await axios.put(
          `${API_BASE_URL}/api/admin/quiz/questions/${editingQuestion._id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Question updated successfully!');
      } else {
        const endpoint = submitData.mode === 'date' 
          ? '/api/admin/quiz/questions/date'
          : '/api/admin/quiz/questions';
        
        await axios.post(
          `${API_BASE_URL}${endpoint}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Question added successfully!');
      }
      
      if (mode === 'date') {
        fetchQuestionsByDate(selectedDate);
        fetchQuizDates();
      } else {
        fetchPoolQuestions();
      }
      resetForm();
    } catch (error) {
      console.error('Error saving question:', error);
      alert(`Failed to save question: ${error.response?.data?.message || error.message}`);
    }
  };


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`${API_BASE_URL}/api/admin/quiz/questions/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Question deleted successfully!');
        if (mode === 'date') {
          fetchQuestionsByDate(selectedDate);
          fetchQuizDates();
        } else {
          fetchPoolQuestions();
        }
      } catch (error) {
        alert(`Failed to delete question: ${error.response?.data?.message || error.message}`);
      }
    }
  };


  const handleDeleteAllForDate = async () => {
    if (window.confirm(`Are you sure you want to delete all questions for ${selectedDate}?`)) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`${API_BASE_URL}/api/admin/quiz/questions/date/${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('All questions deleted successfully!');
        fetchQuestionsByDate(selectedDate);
        fetchQuizDates();
      } catch (error) {
        alert(`Failed to delete questions: ${error.response?.data?.message || error.message}`);
      }
    }
  };


  const handleBulkDeletePool = async () => {
    const confirmMsg = selectedCategory === 'all'
      ? 'Are you sure you want to delete ALL pool questions?'
      : `Are you sure you want to delete all ${selectedCategory} pool questions?`;
    
    if (window.confirm(confirmMsg)) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.post(
          `${API_BASE_URL}/api/admin/quiz/questions/bulk-delete`,
          { mode: 'pool', category: selectedCategory !== 'all' ? selectedCategory : undefined },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Questions deleted successfully!');
        fetchPoolQuestions();
      } catch (error) {
        alert(`Failed to delete questions: ${error.response?.data?.message || error.message}`);
      }
    }
  };


  const resetForm = () => {
    setFormData({
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      category: 'general',
      difficulty: 'medium',
      mode: mode,
      quizDate: mode === 'date' ? selectedDate : undefined
    });
    setShowAddForm(false);
    setEditingQuestion(null);
  };


  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question,
      options: question.options,
      correctAnswer: question.correctAnswer,
      category: question.category,
      difficulty: question.difficulty,
      mode: question.mode || 'date',
      quizDate: question.quizDate ? new Date(question.quizDate).toISOString().split('T')[0] : selectedDate
    });
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };


  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData({ ...formData, quizDate: date });
  };


  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return 'Unlimited';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const loadBulkImportExample = () => {
    const example = {
      category: "Football",
      questions: [
        {
          question: "Who won FIFA 2022?",
          options: ["France", "Argentina", "Brazil", "Spain"],
          answer: "Argentina",
          difficulty: "medium"
        },
        {
          question: "Which club is called The Red Devils?",
          options: ["Liverpool", "Chelsea", "Man United", "PSG"],
          answer: "Man United",
          difficulty: "easy"
        }
      ]
    };
    setBulkImportData(JSON.stringify(example, null, 2));
  };

  const handleCloseBulkImport = () => {
    setShowBulkImport(false);
    setBulkImportData('');
    setBulkImportError(null);
    setBulkImportResult(null);
  };


  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Quiz Management</h2>
          
          <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
            {mode === 'date' && (
              <button
                onClick={() => setShowSettingsForm(!showSettingsForm)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                ⚙️ Date Settings
              </button>
            )}
            {mode === 'pool' && (
              <button
                onClick={() => setShowPoolSettings(!showPoolSettings)}
                className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                ⚙️ Pool Settings
              </button>
            )}
            <button
              onClick={() => setShowBulkImport(!showBulkImport)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              📥 Bulk Import
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              {showAddForm ? 'Cancel' : 'Add Question'}
            </button>
            {questions.length > 0 && (
              <button
                onClick={mode === 'date' ? handleDeleteAllForDate : handleBulkDeletePool}
                className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                🗑️ Delete All
              </button>
            )}
          </div>
        </div>


        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border mb-4">
          <h3 className="font-bold text-lg mb-3">Question Mode</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                setMode('date');
                setFormData({ ...formData, mode: 'date', quizDate: selectedDate });
              }}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                mode === 'date' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              📅 Date-Specific Questions
              <p className="text-xs mt-1 opacity-80">Show questions on specific dates</p>
            </button>
            <button
              onClick={() => {
                setMode('pool');
                setFormData({ ...formData, mode: 'pool' });
              }}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                mode === 'pool' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              🔄 Pool Questions (Evergreen)
              <p className="text-xs mt-1 opacity-80">Randomly shown daily</p>
            </button>
          </div>
          
          {poolSettings.isEnabled && (
            <div className="mt-3 p-3 bg-yellow-100 border-l-4 border-yellow-500 rounded">
              <p className="text-sm font-bold">⚠️ Pool Mode is Currently Active</p>
              <p className="text-xs">Date-specific questions will not be shown to users.</p>
            </div>
          )}
        </div>


        {mode === 'date' && (
          <>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date for Quiz Questions
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="text-gray-600">
                    {questions.length} question{questions.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-blue-600">
                    Timer: {formatTime(quizSettings.timeLimit)}
                  </span>
                  <span className="text-green-600">
                    Attempts: {quizSettings.maxAttempts || 'Unlimited'}
                  </span>
                </div>
              </div>
            </div>


            {showSettingsForm && (
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border mb-4">
                <h3 className="text-lg font-semibold mb-4">
                  Quiz Settings for {new Date(selectedDate).toLocaleDateString()}
                </h3>
                <form onSubmit={handleSettingsSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time Limit (seconds)
                    </label>
                    <input
                      type="number"
                      min="60"
                      value={quizSettings.timeLimit || ''}
                      onChange={(e) => setQuizSettings({ 
                        ...quizSettings, 
                        timeLimit: e.target.value === '' ? null : parseInt(e.target.value)
                      })}
                      placeholder="Leave empty for unlimited"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {quizSettings.timeLimit ? `= ${formatTime(quizSettings.timeLimit)}` : 'Unlimited time'}
                    </p>
                  </div>


                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Attempts per Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quizSettings.maxAttempts || ''}
                      onChange={(e) => setQuizSettings({
                        ...quizSettings,
                        maxAttempts: e.target.value === '' ? null : parseInt(e.target.value)
                      })}
                      placeholder="Leave empty for unlimited"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {quizSettings.maxAttempts ? `${quizSettings.maxAttempts} attempts max` : 'Unlimited attempts'}
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg"
                    >
                      Save Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSettingsForm(false)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}


            {quizDates.length > 0 && (
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Quiz Dates</h3>
                <div className="flex flex-wrap gap-2">
                  {quizDates.map((dateInfo, index) => (
                    <button
                      key={index}
                      onClick={() => handleDateChange(new Date(dateInfo.date).toISOString().split('T')[0])}
                      className={`px-3 py-1 rounded-full text-xs transition-colors ${
                        selectedDate === new Date(dateInfo.date).toISOString().split('T')[0]
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {new Date(dateInfo.date).toLocaleDateString()} ({dateInfo.count})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}


        {mode === 'pool' && (
          <>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.name} value={cat.name}>
                    {cat.displayName}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-600 mt-2">
                {questions.length} pool question{questions.length !== 1 ? 's' : ''} 
                {selectedCategory !== 'all' && ` in ${selectedCategory}`}
              </p>
            </div>


            {showPoolSettings && (
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border mb-4">
                <h3 className="text-lg font-semibold mb-4">Quiz Pool Settings</h3>
                <p className="text-sm text-gray-600 mb-4">
                  For detailed pool configuration, please visit the <strong>Quiz Pool Settings</strong> page.
                </p>
                <form onSubmit={handlePoolSettingsSubmit} className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={poolSettings.isEnabled}
                        onChange={(e) => setPoolSettings({ ...poolSettings, isEnabled: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="font-bold">Enable Pool Mode</span>
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      When enabled, questions are randomly selected from the pool daily
                    </p>
                  </div>


                  {poolSettings.isEnabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Questions Per Day
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={poolSettings.questionsPerDay}
                        onChange={(e) => setPoolSettings({ ...poolSettings, questionsPerDay: parseInt(e.target.value) })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
                    >
                      Save Pool Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPoolSettings(false)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>


      <QuizBulkImportPanel
        show={showBulkImport}
        bulkImportData={bulkImportData}
        setBulkImportData={setBulkImportData}
        loadBulkImportExample={loadBulkImportExample}
        handleBulkImport={handleBulkImport}
        bulkImportLoading={bulkImportLoading}
        onClose={handleCloseBulkImport}
        bulkImportResult={bulkImportResult}
        bulkImportError={bulkImportError}
      />


      {showAddForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4">
            {editingQuestion ? 'Edit Question' : 'Add New Question'}
            <span className="block sm:inline text-sm font-normal text-gray-600 sm:ml-2 mt-1 sm:mt-0">
              (Each question = 10 marks)
            </span>
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Mode
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="date"
                    checked={formData.mode === 'date'}
                    onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                    className="mr-2"
                  />
                  Date-Specific
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="pool"
                    checked={formData.mode === 'pool'}
                    onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                    className="mr-2"
                  />
                  Pool (Evergreen)
                </label>
              </div>
            </div>


            {formData.mode === 'date' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quiz Date
                </label>
                <input
                  type="date"
                  value={formData.quizDate}
                  onChange={(e) => setFormData({ ...formData, quizDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={formData.mode === 'date'}
                />
              </div>
            )}


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {categories.map((cat) => (
                  <option key={cat.name} value={cat.name}>
                    {cat.displayName}
                  </option>
                ))}
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question
              </label>
              <textarea
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                required
              />
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options
              </label>
              {formData.options.map((option, index) => (
                <div key={index} className="mb-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={formData.correctAnswer === index}
                      onChange={() => setFormData({ ...formData, correctAnswer: index })}
                      className="text-blue-600 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-500 mt-1">Select the correct answer</p>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>


            <div className="flex flex-col xs:flex-row gap-3">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}


      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-3 sm:p-4 border-b">
          <h3 className="text-base sm:text-lg font-semibold">
            {mode === 'date' 
              ? `Questions for ${new Date(selectedDate).toLocaleDateString()}`
              : `Pool Questions ${selectedCategory !== 'all' ? `(${selectedCategory})` : ''}`
            }
          </h3>
          <p className="text-sm text-gray-600">Each question is worth 10 marks</p>
        </div>


        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading questions...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
            <p className="text-gray-600 mb-4">Add some questions to get started</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Add First Question
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {questions.map((question, index) => (
              <div key={question._id} className="p-4 hover:bg-gray-50">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        Q{index + 1} (10 marks)
                      </span>
                      <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                        {question.category}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                        question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {question.difficulty}
                      </span>
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                        {question.mode === 'date' 
                          ? `📅 ${new Date(question.quizDate).toLocaleDateString()}`
                          : '🔄 Pool'
                        }
                      </span>
                    </div>
                    
                    <h4 className="font-medium text-gray-900 mb-2">{question.question}</h4>
                    
                    <div className="space-y-1">
                      {question.options.map((option, optIndex) => (
                        <div key={optIndex} className={`text-sm flex items-start gap-2 ${
                          optIndex === question.correctAnswer ? 'text-green-600 font-medium' : 'text-gray-600'
                        }`}>
                          <span className="flex-shrink-0">{String.fromCharCode(65 + optIndex)}.</span>
                          <span className="flex-1">{option}</span>
                          {optIndex === question.correctAnswer && <span className="text-green-600 flex-shrink-0">✓</span>}
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-2 text-xs text-gray-500">
                      Created by {question.createdBy?.username} on {new Date(question.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="flex lg:flex-col gap-2">
                    <button
                      onClick={() => handleEdit(question)}
                      className="flex-1 lg:flex-none bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg"
                      title="Edit"
                    >
                      <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(question._id)}
                      className="flex-1 lg:flex-none bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg"
                      title="Delete"
                    >
                      <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


export default QuizManagement;
